/**
 * 配送状況取得カスタムフック
 *
 * fulfillment IDの配列から配送状況を取得する
 * 各リクエストが完了するたびに順次結果を返す（プログレッシブアップデート）
 */

import {
  LegacyPortalShippingService,
  LegacyPortalShippingStatusDispatchStatus,
  LegacyPortalShippingStatus,
} from "@spwn-portal/grpc";
import useSWR from "swr";
import { useConnectClient } from "utility/connectWeb";
import { z } from "zod";

export const fulfillmentFirestoreId = z
  .string()
  .brand("fulfillmentFirestoreId");
type FulfillmentFirestoreId = z.infer<typeof fulfillmentFirestoreId>;

/**
 * 配送状況を取得するカスタムフック
 *
 * @param fulfillmentFirestoreIds - FulfillmentのFirestore IDの配列
 * @returns 配送状況のMap、エラー、ローディング状態
 *
 * @example
 * const { shippingStatuses, error, isLoading } = useShippingStatus(['id1', 'id2']);
 */
export const useShippingStatus = (fulfillmentFirestoreIds: string[]) => {
  const client = useConnectClient(LegacyPortalShippingService);

  const key =
    fulfillmentFirestoreIds.length > 0
      ? ["shipping_statuses", ...fulfillmentFirestoreIds]
      : null;

  const { data, mutate } = useSWR<
    Map<FulfillmentFirestoreId, LegacyPortalShippingStatus>
  >(key, async () => {
    const resultMap = new Map<
      FulfillmentFirestoreId,
      LegacyPortalShippingStatus
    >();

    // 全てのリクエストを並行して開始
    const fetchPromises = fulfillmentFirestoreIds.map(async (id) => {
      try {
        const response = await client.getShippingStatusByFulfillmentFirestoreId(
          {
            fulfillmentFirestoreId: id,
          }
        );

        if (response.shippingStatus) {
          // 各リクエストが完了するたびに結果を追加
          resultMap.set(
            fulfillmentFirestoreId.parse(id),
            response.shippingStatus
          );
          // SWRのキャッシュを即時更新
          void mutate(new Map(resultMap), { revalidate: false });
        }
      } catch (err) {
        // エラーが発生した場合は不明として結果を反映
        resultMap.set(
          fulfillmentFirestoreId.parse(id),
          new LegacyPortalShippingStatus({
            status: LegacyPortalShippingStatusDispatchStatus.UNSPECIFIED,
          })
        );
        void mutate(new Map(resultMap), { revalidate: false });
      }
    });

    // 全てのリクエストが完了するまで待つ（成功・失敗に関わらず）
    await Promise.allSettled(fetchPromises);

    return resultMap;
  });

  return {
    shippingStatuses:
      data ?? new Map<FulfillmentFirestoreId, LegacyPortalShippingStatus>(),
  };
};
